// Copyright ©️ 2025 eVolve MEP, LLC

import React, { useState } from 'react';

import { type HexTriplet, isHexTriplet } from '@evolvemep/api-ts';
import { ColorPicker, Box, Popover, ColorSwatch, InputLabel, ActionIcon } from '@mantine/core';
import { useUncontrolled } from '@mantine/hooks';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import barClasses from 'components/Mantine/colorPickerBar.module.css';

type Props = {
  label?: string;
  value: HexTriplet;
  onChange: (value: HexTriplet) => void;
  transparent?: boolean;
};

export const ColorPickerBar = ({ label = 'Color', value, onChange, transparent }: Props) => {
  const [opened, setOpened] = useState(false);
  const [internalColor, setInternalColor] = useUncontrolled<string>({ value });
  return (
    <Popover onChange={setOpened} opened={opened}>
      <Popover.Target>
        <Box w="0.5em" style={{ overflow: 'visible' }}>
          <ColorSwatch
            className={`${barClasses.colorBox} ${opened ? barClasses.active : ''}`}
            color={`${value}${transparent ? '6f' : ''}`}
            radius={0}
            size="0.5em"
            withShadow={false}
            onClick={() => setOpened((o) => !o)}
          />
        </Box>
      </Popover.Target>
      <Popover.Dropdown>
        <ActionIcon onClick={() => setOpened(false)} style={{ position: 'absolute', right: 10, top: 10 }}>
          <EvolveIcon icon="Close" size="sm" color="dark" />
        </ActionIcon>
        <InputLabel mb={4}>{label}</InputLabel>
        <ColorPicker
          value={internalColor}
          onChange={setInternalColor}
          onChangeEnd={(newValue) => onChange(isHexTriplet(newValue) ? newValue : value)}
        />
      </Popover.Dropdown>
    </Popover>
  );
};
